import { useState, ReactNode, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import GeneratedDocumentsList from '../components/GeneratedDocumentsList'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'

// ─────────────────── Types ───────────────────
type Status = 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'TERMINATED'
type ContractType = 'NDA' | 'MSA' | 'SOW' | 'ICA' | 'DPA' | 'PO' | 'AMENDMENT'
type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED'
type RateUnit = 'HOUR' | 'DAY'
type BillingCadence = 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'MILESTONE'

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }

type ContractStub = { id: number; contract_number: string; title: string; partner: number; contract_type: ContractType }

type ContractEvent = {
  id: number
  event_type: 'CREATED' | 'STATUS_CHANGED' | 'APPROVAL_CHANGED' | 'UPDATED' | 'NOTE'
  field_name: string
  old_value: string
  new_value: string
  note: string
  changed_by: string
  created_at: string
}

type VendorContract = {
  id: number
  contract_number: string
  title: string
  // type & hierarchy
  contract_type: ContractType
  parent_contract: number | null
  parent_contract_number: string | null
  version: string
  // partner
  partner: number
  partner_name?: string
  partner_code?: string
  // signatures
  signed_at: string | null
  counterparty_signed_at: string | null
  counterparty_signatory: string
  // dates
  start_date: string
  end_date: string | null
  // approval
  approval_status: ApprovalStatus
  approved_by: string
  approved_at: string | null
  // rate & billing
  currency_code: string
  default_rate: string
  rate_unit: RateUnit
  max_hours: string | null
  billing_cadence: BillingCadence
  payment_terms_days: number
  // obligations
  ip_assignment: boolean
  non_compete: boolean
  sla_terms: string
  renewal_notice_days: number
  // misc
  status: Status
  document_url: string
  notes: string
  is_currently_active?: boolean
  assignment_count?: number
}

type Assignment = {
  id: number
  project: number
  project_code: string
  project_name: string
  employee: number
  employee_name: string
  role: 'CONSULTANT' | 'CONTRACTOR' | 'MANAGER'
  vendor_contract: number | null
  rate_override: string | null
  effective_rate: string
  effective_rate_unit: RateUnit
  effective_currency: string
  max_hours: string | null
  hours_logged: string
  hours_approved: string
  start_date: string
  end_date: string | null
  status: 'ACTIVE' | 'PAUSED' | 'ENDED'
}

// ─────────────────── Constants ───────────────────
const STATUS_COLOR: Record<Status, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-amber-100 text-amber-800',
  TERMINATED: 'bg-red-100 text-red-700',
}

const CONTRACT_TYPE_BADGE: Record<ContractType, string> = {
  NDA: 'bg-slate-100 text-slate-700',
  MSA: 'bg-blue-100 text-blue-800',
  SOW: 'bg-indigo-100 text-indigo-800',
  ICA: 'bg-teal-100 text-teal-800',
  DPA: 'bg-purple-100 text-purple-800',
  PO: 'bg-amber-100 text-amber-800',
  AMENDMENT: 'bg-orange-100 text-orange-800',
}

const CONTRACT_TYPE_OPTIONS: { value: ContractType; label: string }[] = [
  { value: 'NDA', label: 'NDA — Non-Disclosure Agreement' },
  { value: 'MSA', label: 'MSA — Master Service Agreement' },
  { value: 'SOW', label: 'SOW — Statement of Work' },
  { value: 'ICA', label: 'ICA — Independent Contractor Agreement' },
  { value: 'DPA', label: 'DPA — Data Processing Agreement (GDPR)' },
  { value: 'PO', label: 'PO — Purchase Order' },
  { value: 'AMENDMENT', label: 'Amendment / Addendum' },
]

const APPROVAL_COLOR: Record<ApprovalStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
}

const CADENCE_LABEL: Record<BillingCadence, string> = {
  MONTHLY: 'Monthly',
  BIWEEKLY: 'Bi-weekly',
  WEEKLY: 'Weekly',
  MILESTONE: 'Per milestone',
}

const ASSIGNMENT_STATUS_COLOR: Record<Assignment['status'], string> = {
  ACTIVE: 'bg-blue-100 text-blue-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  ENDED: 'bg-slate-100 text-slate-700',
}

type TabKey = 'details' | 'assignments' | 'documents' | 'files' | 'history'

// ─────────────────── Page ───────────────────
export default function FinanceVendorContractDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const contractId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const contractQ = useQuery({
    queryKey: ['finance', 'vendor-contract', contractId],
    queryFn: async () => (await api.get<VendorContract>(`/partners/vendor-contracts/${contractId}/`)).data,
    enabled: !!contractId,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  // All contracts — used for parent contract picker
  const contractsQ = useQuery({
    queryKey: ['finance', 'vendor-contracts'],
    queryFn: async () => (await api.get<ContractStub[]>('/partners/vendor-contracts/')).data,
  })
  const assignmentsQ = useQuery({
    queryKey: ['consulting', 'assignments'],
    queryFn: async () => (await api.get<Assignment[]>('/consulting/assignments/')).data,
  })
  const eventsQ = useQuery({
    queryKey: ['finance', 'vendor-contract-events', contractId],
    queryFn: async () => (await api.get<ContractEvent[]>(`/partners/vendor-contracts/${contractId}/events/`)).data,
    enabled: !!contractId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<VendorContract>) =>
      (await api.patch(`/partners/vendor-contracts/${contractId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'vendor-contract', contractId] })
      qc.invalidateQueries({ queryKey: ['finance', 'vendor-contracts'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/partners/vendor-contracts/${contractId}/`),
    onSuccess: () => {
      toast.success('Contract deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'vendor-contracts'] })
      navigate('/admin/partners/vendor-contracts')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (contractQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading&hellip;</p></AdminLayout>
  }
  if (contractQ.isError || !contractQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Vendor contract not found.</p>
          <Link to="/admin/partners/vendor-contracts" className="text-blue-600 hover:underline">&#8592; Back to vendor contracts</Link>
        </div>
      </AdminLayout>
    )
  }

  const contract = contractQ.data
  const vendorPartners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'VENDOR' || p.partner_type === 'BOTH' || p.partner_type === 'FREELANCER')
  )
  // Sibling contracts from the same vendor (excluding self) — for parent picker
  const peerContracts = (contractsQ.data || []).filter(
    (c) => c.id !== contractId && c.partner === contract.partner
  )
  const linkedAssignments = (assignmentsQ.data || []).filter((a) => a.vendor_contract === contract.id)
  const hasAssignments = linkedAssignments.length > 0

  const save = (payload: Partial<VendorContract>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  // Expiry alert
  const daysUntilExpiry: number | null = (contract.status === 'ACTIVE' && contract.end_date)
    ? (() => {
        const today = new Date(); today.setHours(0, 0, 0, 0)
        const end = new Date(contract.end_date!); end.setHours(0, 0, 0, 0)
        return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
      })()
    : null
  const showExpiryAlert = daysUntilExpiry !== null
    && daysUntilExpiry <= (contract.renewal_notice_days ?? 30)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/partners/vendor-contracts" className="text-muted-foreground hover:text-foreground hover:underline">&#8592; Vendor contracts</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              {contract.contract_number}
              <span className={`px-2 py-0.5 rounded font-semibold text-xs ${CONTRACT_TYPE_BADGE[contract.contract_type] ?? 'bg-muted text-muted-foreground'}`}>
                {contract.contract_type}
              </span>
              {contract.version && (
                <span className="text-muted-foreground">v{contract.version}</span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText
                value={contract.title}
                onSave={(v) => save({ title: v })}
                placeholder="Untitled contract"
              />
            </h2>
            <div className="text-sm text-muted-foreground">
              {contract.partner_name && (
                <Link to={`/admin/partners/${contract.partner}`} className="hover:underline">
                  {contract.partner_name}
                </Link>
              )}
              {contract.parent_contract_number && (
                <>
                  <span> &middot; under </span>
                  <Link to={`/admin/partners/vendor-contracts/${contract.parent_contract}`} className="hover:underline font-mono">
                    {contract.parent_contract_number}
                  </Link>
                </>
              )}
              <span> &middot; </span>
              <span className="font-mono">
                {Number(contract.default_rate).toFixed(2)} {contract.currency_code} / {contract.rate_unit === 'DAY' ? 'day' : 'hour'}
              </span>
              <span> &middot; </span>
              <span>{contract.start_date} &rarr; {contract.end_date || 'open'}</span>
              {contract.is_currently_active && (
                <span className="ml-2 text-xs text-green-700">&#9679; currently active</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GenerateDocumentButton
              sourceType="partners.VendorContract"
              sourceId={contractId}
              onGenerated={() => qc.invalidateQueries({ queryKey: ['generated-documents', 'partners.VendorContract', contractId] })}
            />
            <InlineStatus
              value={contract.status}
              colorMap={STATUS_COLOR}
              options={contract.contract_type === 'AMENDMENT'
                ? [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'ACTIVE', label: 'Executed' },
                    { value: 'TERMINATED', label: 'Superseded' },
                  ]
                : [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'EXPIRED', label: 'Expired' },
                    { value: 'TERMINATED', label: 'Terminated' },
                  ]}
              onSave={(v) => save({ status: v })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={hasAssignments}
                  onClick={() => !hasAssignments && confirm(
                    `vendor contract ${contract.contract_number}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!hasAssignments ? 'text-destructive' : ''}
                >
                  {hasAssignments
                    ? `Delete (${linkedAssignments.length} assignment${linkedAssignments.length === 1 ? '' : 's'})`
                    : 'Delete contract'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Expiry alert banner */}
      {showExpiryAlert && daysUntilExpiry !== null && (
        <div className={`mb-4 px-4 py-3 rounded-lg border text-sm ${
          daysUntilExpiry < 0
            ? 'bg-red-50 border-red-300 text-red-800'
            : daysUntilExpiry <= 7
            ? 'bg-red-50 border-red-300 text-red-800'
            : 'bg-amber-50 border-amber-300 text-amber-800'
        }`}>
          <strong>
            {daysUntilExpiry < 0
              ? `This contract expired ${Math.abs(daysUntilExpiry)} day${Math.abs(daysUntilExpiry) === 1 ? '' : 's'} ago`
              : daysUntilExpiry === 0
              ? 'This contract expires today'
              : `This contract expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`}
          </strong>
          {' '}({contract.end_date}).{' '}
          Renewal notice period is <strong>{contract.renewal_notice_days} days</strong>.{' '}
          {daysUntilExpiry >= 0
            ? 'Renew or set status to TERMINATED before the deadline.'
            : 'Update status to EXPIRED or TERMINATED.'}
        </div>
      )}

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'assignments', 'documents', 'files', 'history'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'assignments' && `Assignments (${linkedAssignments.length})`}
              {t === 'documents' && 'Documents'}
              {t === 'files' && 'Files'}
              {t === 'history' && `History${eventsQ.data ? ` (${eventsQ.data.length})` : ''}`}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          contract={contract}
          vendorPartners={vendorPartners}
          peerContracts={peerContracts}
          onSave={save}
        />
      )}
      {tab === 'assignments' && (
        <AssignmentsTab assignments={linkedAssignments} loading={assignmentsQ.isLoading} />
      )}
      {tab === 'documents' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground max-w-2xl">
              Documents generated from templates for this contract. Use the{' '}
              <strong>Generate document</strong> button above to create a new one.
            </p>
            <GenerateDocumentButton
              sourceType="partners.VendorContract"
              sourceId={contractId}
              label="Generate document..."
              onGenerated={() => qc.invalidateQueries({ queryKey: ['generated-documents', 'partners.VendorContract', contractId] })}
            />
          </div>
          <GeneratedDocumentsList sourceType="partners.VendorContract" sourceId={contractId} />
        </div>
      )}
      {tab === 'files' && <FilesTab parent="finance.vendorcontract" parentId={contractId} />}
      {tab === 'history' && (
        <HistoryTab
          contractId={contractId}
          events={eventsQ.data || []}
          loading={eventsQ.isLoading}
          onNoteAdded={() => eventsQ.refetch()}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  contract, vendorPartners, peerContracts, onSave,
}: {
  contract: VendorContract
  vendorPartners: Partner[]
  peerContracts: ContractStub[]
  onSave: (payload: Partial<VendorContract>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Contract header ── */}
      <Section title="Contract header">
        <Row label="Contract number">
          <InlineText
            value={contract.contract_number}
            onSave={(v) => onSave({ contract_number: v })}
            className="font-mono"
          />
        </Row>
        <Row label="Title">
          <InlineText
            value={contract.title}
            onSave={(v) => onSave({ title: v })}
            placeholder="Untitled contract"
          />
        </Row>
        <Row label="Vendor">
          <InlineSelect
            value={String(contract.partner)}
            displayLabel={contract.partner_name || <Dash />}
            options={vendorPartners.map((p) => ({
              value: String(p.id),
              label: `${p.code} ${p.name}`,
              searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: Number(v) })}
            helper="Active VENDOR / BOTH / FREELANCER partners."
          />
        </Row>
        <Row label="Status">
          <InlineStatus
            value={contract.status}
            colorMap={STATUS_COLOR}
            options={contract.contract_type === 'AMENDMENT'
              ? [
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'ACTIVE', label: 'Executed' },
                  { value: 'TERMINATED', label: 'Superseded' },
                ]
              : [
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'EXPIRED', label: 'Expired' },
                  { value: 'TERMINATED', label: 'Terminated' },
                ]}
            onSave={(v) => onSave({ status: v })}
          />
        </Row>
        <Row label="Document URL">
          <InlineText
            value={contract.document_url}
            onSave={(v) => onSave({ document_url: v })}
            placeholder="https://..."
            helper="Optional link to the signed PDF or shared drive."
          />
        </Row>
      </Section>

      {/* ── Contract type & hierarchy ── */}
      <Section title="Contract type & hierarchy">
        <Row label="Type">
          <InlineSelect
            value={contract.contract_type}
            displayLabel={
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${CONTRACT_TYPE_BADGE[contract.contract_type] ?? 'bg-muted text-muted-foreground'}`}>
                {contract.contract_type}
              </span>
            }
            options={CONTRACT_TYPE_OPTIONS}
            onSave={(v) => onSave({ contract_type: v as ContractType })}
            helper="NDA, MSA, SOW, ICA, DPA, PO, or Amendment."
          />
        </Row>
        <Row label="Version">
          <InlineText
            value={contract.version}
            onSave={(v) => onSave({ version: v })}
            placeholder="1.0"
            className="font-mono"
            helper="Increment when an amendment or addendum is issued."
          />
        </Row>
        <Row label="Parent contract">
          <InlineSelect
            value={contract.parent_contract ? String(contract.parent_contract) : ''}
            displayLabel={
              contract.parent_contract_number ? (
                <Link
                  to={`/admin/partners/vendor-contracts/${contract.parent_contract}`}
                  className="text-blue-600 hover:underline font-mono text-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  {contract.parent_contract_number}
                </Link>
              ) : <span className="text-muted-foreground italic text-xs">none</span>
            }
            allowClear
            options={peerContracts.map((c) => ({
              value: String(c.id),
              label: c.contract_number,
              searchText: `${c.contract_number} ${c.title}`,
            }))}
            onSave={(v) => onSave({ parent_contract: v ? Number(v) : null } as Partial<VendorContract>)}
            helper="The MSA or ICA this SOW / Amendment is issued under. Clear to remove."
          />
        </Row>
      </Section>

      {/* ── Approval ── */}
      <Section title="Approval">
        <Row label="Approval status">
          <InlineStatus
            value={contract.approval_status}
            colorMap={APPROVAL_COLOR}
            options={[
              { value: 'PENDING', label: 'Pending approval' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
            onSave={(v) => onSave({ approval_status: v as ApprovalStatus })}
          />
        </Row>
        <Row label="Approved by">
          <InlineText
            value={contract.approved_by}
            onSave={(v) => onSave({ approved_by: v })}
            placeholder="Name or email of approver"
          />
        </Row>
        <Row label="Approved on">
          <InlineDate value={contract.approved_at} onSave={(v) => onSave({ approved_at: v })} />
        </Row>
      </Section>

      {/* ── Signatures ── */}
      <Section title="Signatures">
        <Row label="Our signed date">
          <InlineDate value={contract.signed_at} onSave={(v) => onSave({ signed_at: v })} />
        </Row>
        <Row label="Counterparty signed">
          <InlineDate value={contract.counterparty_signed_at} onSave={(v) => onSave({ counterparty_signed_at: v })} />
        </Row>
        <Row label="Counterparty signatory">
          <InlineText
            value={contract.counterparty_signatory}
            onSave={(v) => onSave({ counterparty_signatory: v })}
            placeholder="Full name of the person who signed"
          />
        </Row>
      </Section>

      {/* ── Dates ── */}
      <Section title="Dates">
        <Row label="Start date">
          <InlineDate
            value={contract.start_date}
            onSave={(v) => onSave({ start_date: v || contract.start_date })}
          />
        </Row>
        <Row label="End date">
          <InlineDate value={contract.end_date} onSave={(v) => onSave({ end_date: v })} />
        </Row>
        <Row label="Renewal notice (days)">
          <InlineNumber
            value={String(contract.renewal_notice_days)}
            step="1"
            onSave={(v) => onSave({ renewal_notice_days: Number(v) })}
            displayFormat={(v) => <span className="font-mono">{Number(v || 30)} days</span>}
            helper="Minimum notice required before expiry for renewal or termination."
          />
        </Row>
      </Section>

      {/* ── Rate & billing (hidden for NDA, DPA, Amendment — no monetary component) ── */}
      {!['NDA', 'DPA', 'AMENDMENT'].includes(contract.contract_type) && (
        <Section title="Rate & billing">
          <Row label="Currency">
            <InlineText
              value={contract.currency_code}
              onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
              className="font-mono"
            />
          </Row>
          <Row label="Default rate">
            <InlineNumber
              value={contract.default_rate}
              onSave={(v) => onSave({ default_rate: v })}
              displayFormat={(v) => (
                <span className="font-mono">
                  {Number(v || 0).toFixed(2)} {contract.currency_code} / {contract.rate_unit === 'DAY' ? 'day' : 'hour'}
                </span>
              )}
            />
          </Row>
          <Row label="Rate unit">
            <InlineSelect
              value={contract.rate_unit}
              options={[
                { value: 'HOUR', label: 'Hour' },
                { value: 'DAY', label: 'Day' },
              ]}
              onSave={(v) => onSave({ rate_unit: v as RateUnit })}
            />
          </Row>
          <Row label="Max hours (cap)">
            <InlineNumber
              value={contract.max_hours || ''}
              onSave={(v) => onSave({ max_hours: v === '' ? null : v } as Partial<VendorContract>)}
              displayFormat={(v) => v
                ? <span className="font-mono">{Number(v).toFixed(2)}</span>
                : <span className="text-muted-foreground italic">uncapped</span>}
              helper="Optional. Caps cumulative billable hours across all assignments under this contract."
            />
          </Row>
          <Row label="Billing cadence">
            <InlineSelect
              value={contract.billing_cadence}
              displayLabel={CADENCE_LABEL[contract.billing_cadence]}
              options={(Object.keys(CADENCE_LABEL) as BillingCadence[]).map((k) => ({
                value: k, label: CADENCE_LABEL[k],
              }))}
              onSave={(v) => onSave({ billing_cadence: v as BillingCadence })}
            />
          </Row>
          <Row label="Payment terms">
            <InlineNumber
              value={String(contract.payment_terms_days)}
              step="1"
              onSave={(v) => onSave({ payment_terms_days: Number(v) })}
              displayFormat={(v) => <span className="font-mono">Net {Number(v || 0)} days</span>}
            />
          </Row>
        </Section>
      )}

      {/* ── Obligations & compliance ── */}
      <Section title="Obligations & compliance">
        <Row label="IP assignment">
          <InlineStatus
            value={contract.ip_assignment ? 'YES' : 'NO'}
            colorMap={{ YES: 'bg-green-100 text-green-800', NO: 'bg-gray-100 text-gray-600' }}
            options={[
              { value: 'YES', label: 'Yes — IP assigned' },
              { value: 'NO', label: 'No' },
            ]}
            onSave={(v) => onSave({ ip_assignment: v === 'YES' })}
            helper="Contract includes an intellectual property assignment clause."
          />
        </Row>
        <Row label="Non-compete">
          <InlineStatus
            value={contract.non_compete ? 'YES' : 'NO'}
            colorMap={{ YES: 'bg-blue-100 text-blue-800', NO: 'bg-gray-100 text-gray-600' }}
            options={[
              { value: 'YES', label: 'Yes — non-compete applies' },
              { value: 'NO', label: 'No' },
            ]}
            onSave={(v) => onSave({ non_compete: v === 'YES' })}
            helper="Contract includes a non-compete or non-solicitation clause."
          />
        </Row>
        <Row label="SLA terms">
          <InlineText
            value={contract.sla_terms}
            multiline
            onSave={(v) => onSave({ sla_terms: v })}
            placeholder="Describe SLA commitments, KPIs, or penalty clauses"
          />
        </Row>
      </Section>

      {/* ── Notes ── */}
      <Section title="Notes">
        <Row label="Internal notes">
          <InlineText
            value={contract.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Internal notes about this contract"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[200px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Assignments tab ───────────────────

function AssignmentsTab({
  assignments, loading,
}: {
  assignments: Assignment[]
  loading: boolean
}) {
  const navigate = useNavigate()

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading&hellip;</p>
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground max-w-2xl">
        Project assignments linked to this contract. Click a row to open the project record page where you can edit assignment details, rates, and end dates.
      </p>

      {assignments.length === 0 ? (
        <p className="text-muted-foreground text-sm">No assignments use this contract yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Project</Th>
                <Th>Person</Th>
                <Th>Role</Th>
                <Th>Effective rate</Th>
                <Th>Hours (approved / logged)</Th>
                <Th>Period</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr
                  key={a.id}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/consulting/projects/${a.project}`)}
                >
                  <Td>
                    <span className="text-blue-600 hover:underline font-medium">
                      {a.project_code} &mdash; {a.project_name}
                    </span>
                  </Td>
                  <Td>{a.employee_name}</Td>
                  <Td className="text-xs">{a.role}</Td>
                  <Td className="text-xs font-mono">
                    {Number(a.effective_rate).toFixed(2)} {a.effective_currency} / {a.effective_rate_unit === 'DAY' ? 'day' : 'hour'}
                    {a.rate_override && <span className="text-amber-700 ml-1">(override)</span>}
                  </Td>
                  <Td className="text-xs">
                    <span className="text-green-700 font-semibold">{a.hours_approved}</span>{' / '}
                    <span className="text-muted-foreground">{a.hours_logged}</span>
                    {a.max_hours && <span className="text-muted-foreground"> &middot; cap {a.max_hours}</span>}
                  </Td>
                  <Td className="text-xs">{a.start_date} &rarr; {a.end_date || <span className="text-muted-foreground">open</span>}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${ASSIGNMENT_STATUS_COLOR[a.status]}`}>
                      {a.status}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─────────────────── History tab ───────────────────

const EVENT_DOT: Record<ContractEvent['event_type'], string> = {
  CREATED:          'bg-green-500',
  STATUS_CHANGED:   'bg-blue-500',
  APPROVAL_CHANGED: 'bg-purple-500',
  UPDATED:          'bg-gray-400',
  NOTE:             'bg-amber-400',
}

const EVENT_LABEL: Record<ContractEvent['event_type'], string> = {
  CREATED:          'Created',
  STATUS_CHANGED:   'Status changed',
  APPROVAL_CHANGED: 'Approval changed',
  UPDATED:          'Updated',
  NOTE:             'Note',
}

function fmtDatetime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function HistoryTab({
  contractId, events, loading, onNoteAdded,
}: {
  contractId: number
  events: ContractEvent[]
  loading: boolean
  onNoteAdded: () => void
}) {
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  async function handleAddNote() {
    const text = note.trim()
    if (!text) return
    setSubmitting(true)
    setErr('')
    try {
      await api.post(`/partners/vendor-contracts/${contractId}/add_note/`, { note: text })
      setNote('')
      onNoteAdded()
    } catch (e: unknown) {
      setErr(extractErr(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* Add note box */}
      <div className="bg-card rounded-lg shadow p-4 space-y-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Add note</p>
        <Textarea
          ref={textareaRef}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Record a decision, reminder, or context note about this contract..."
          rows={3}
          className="text-sm"
        />
        {err && <p className="text-xs text-destructive">{err}</p>}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleAddNote}
            disabled={submitting || !note.trim()}
          >
            {submitting ? 'Saving...' : 'Add note'}
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading history&hellip;</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">No history yet.</p>
      ) : (
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />

          <ol className="space-y-4">
            {events.map((ev) => (
              <li key={ev.id} className="relative flex gap-4 pl-8">
                {/* Dot */}
                <span className={`absolute left-0 top-1.5 h-6 w-6 rounded-full flex items-center justify-center ${EVENT_DOT[ev.event_type]}`}>
                  <span className="h-2 w-2 rounded-full bg-white" />
                </span>

                <div className="flex-1 bg-card rounded-lg shadow-sm border border-border px-4 py-3 text-sm">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-semibold text-foreground">
                      {EVENT_LABEL[ev.event_type]}
                      {ev.field_name && (
                        <span className="font-normal text-muted-foreground"> &mdash; {ev.field_name}</span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                      {fmtDatetime(ev.created_at)}
                    </span>
                  </div>

                  {/* Event body */}
                  {ev.event_type === 'CREATED' && (
                    <p className="text-muted-foreground">{ev.new_value}</p>
                  )}
                  {ev.event_type === 'NOTE' && (
                    <p className="text-foreground whitespace-pre-wrap">{ev.note}</p>
                  )}
                  {(ev.event_type === 'STATUS_CHANGED' || ev.event_type === 'APPROVAL_CHANGED' || ev.event_type === 'UPDATED') && (
                    <div className="flex items-center gap-2 text-xs font-mono">
                      <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 line-through">
                        {ev.old_value || <span className="italic not-italic text-muted-foreground">empty</span>}
                      </span>
                      <span className="text-muted-foreground">&rarr;</span>
                      <span className="px-2 py-0.5 rounded bg-green-50 text-green-800">
                        {ev.new_value || <span className="italic not-italic text-muted-foreground">empty</span>}
                      </span>
                    </div>
                  )}

                  {ev.changed_by && (
                    <p className="mt-1 text-xs text-muted-foreground">by {ev.changed_by}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
